import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";
import { AxiosError } from "axios";
import {
  HiOutlineCalendar,
  HiOutlineCreditCard,
  HiOutlineExternalLink,
  HiOutlineGlobeAlt,
  HiOutlineUser,
} from "react-icons/hi";
import { SideDrawer } from "../../../atoms/SideDrawer";
import { CustomButton } from "../../../atoms/CustomButton";
import { CustomInput } from "../../../atoms/CustomInput";
import { CharacterCounter } from "../../../atoms/CharacterCounter";
import OptionToggleRow from "../../../atoms/OptionToggleRow";
import UnlockBadge from "../../../atoms/UnlockBadge";
import { PlansModal } from "../../../modals/PlansModal";
import { AlertMessage } from "../../../atoms/AlertMessage";
import HtmlEditor from "../../../atoms/HtmlEditor";
import EventImageUpload from "../../../atoms/EventImageUpload";
import {
  FoundPerson,
  PersonSearchList,
  usePersonSearch,
} from "../../../molecules/PersonSearch";
import { isHtmlEmpty } from "../todos/todoColumns";
import { richTextLength, richTextProblem } from "../../../../utils/richText";
import { BookingTypeImage } from "../../customRegister/components/BookingTypeDetails";
import useAuth from "../../../../hooks/useAuth";
import { useGetSignupFormsList } from "../../../../hooks/entity";
import { PlanTier, usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";
import {
  AgendaType,
  AgendaTypeInput,
  PaymentCurrency,
  TypePayment,
  useAgendaPaymentsBlocked,
  useCreateAgendaType,
  useGetAgendaTypes,
  useGetMembersSettings,
  useUpdateAgendaType,
} from "../../../../hooks/agenda";
import { useAgendaAccess } from "../../../../hooks/agenda/useAgendaAccess";
import {
  BOOKING_LIMITS,
  DURATION_MINUTE_STEPS,
  durationOf,
  durationParts,
  durationProblem,
  useAgendaFailure,
} from "./agendaTypes";
import { profilePath } from "./ProfileLink";
import {
  PAYMENT_CURRENCIES,
  PAYMENT_LIMITS,
  centsToInput,
  formatPrice,
  inputToCents,
} from "./agendaPayment";
import TypeMembersField, {
  PersonAvailability,
  TypePerson,
} from "./TypeMembersField";

interface FormValues {
  title: string;
  location: string;
  description: string;
  image_url: string;
  members: string[];
  duration_hours: string;
  duration_minutes: number;
  min_notice_hours: number | "";
  horizon_days: number | "";
  one_upcoming: boolean;
  listed: boolean;
  signup_form_id: string;
  paid: boolean;
  amount: string;
  currency: PaymentCurrency;
  unpaid: TypePayment["unpaid"];
}

const DEFAULT_VALUES: FormValues = {
  title: "",
  location: "",
  description: "",
  image_url: "",
  members: [],
  duration_hours: "0",
  duration_minutes: 30,
  min_notice_hours: 2,
  horizon_days: 30,
  one_upcoming: true,
  listed: true,
  signup_form_id: "",
  paid: false,
  amount: "",
  currency: "EUR",
  unpaid: "required",
};

const toValues = (
  type: AgendaType | null | undefined,
  lang: string
): FormValues =>
  type
    ? {
        title: type.title,
        location: type.location ?? "",
        description: type.description ?? "",
        image_url: type.image_url ?? "",
        members: type.members.map((m) => m.user_identification),
        duration_hours: String(durationParts(type.duration_min).hours),
        duration_minutes: durationParts(type.duration_min).minutes,
        min_notice_hours: type.min_notice_hours,
        horizon_days: type.horizon_days,
        one_upcoming: type.max_upcoming_per_client !== null,
        listed: type.listed,
        signup_form_id: type.signup_form_id ?? "",
        paid: Boolean(type.payment),
        amount: type.payment ? centsToInput(type.payment.amount, lang) : "",
        currency: type.payment?.currency ?? "EUR",
        unpaid: type.payment?.unpaid ?? "required",
      }
    : DEFAULT_VALUES;

const fieldClass = (hasError: boolean) =>
  twMerge(
    "block w-full rounded-md border bg-white p-2.5 text-base text-gray-900 shadow-sm md:py-2 md:text-sm disabled:cursor-not-allowed disabled:opacity-50",
    hasError
      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:border-blue-500 focus:ring-blue-500"
  );

const toInteger = (value: string): number | "" => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? "" : parsed;
};

const EVENTS_PATH = "/control-panel/event-payments";
const PAYMENT_MODULE_PATH = "/control-panel/payment-module";

const inRange = (value: number | "", min: number, max: number) =>
  value !== "" && Number.isInteger(value) && value >= min && value <= max;

const fromSearch = (person: FoundPerson): TypePerson => ({
  user_identification: person.user_identification,
  name:
    `${person.first_name} ${person.last_name}`.trim() || person.email || null,
  public_name: person.user_name || person.first_name || null,
});

interface AppointmentTypeDrawerProps {
  open: boolean;
  onClose: () => void;
  type: AgendaType | null;
}

const AppointmentTypeDrawer: React.FC<AppointmentTypeDrawerProps> = ({
  open,
  onClose,
  type,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const failure = useAgendaFailure();
  const { user, customer_identification: customerId } = useAuth();
  const entityId = user.entity_identification;
  const permissions = usePermissions(user);
  const { canConfigure: canEdit, managesOthers } = useAgendaAccess(
    customerId,
    entityId
  );
  const canCreateForm = permissions.can(Cap.SIGNUP_FORMS, Level.WRITE);
  const canSearch = permissions.can(Cap.SEARCH, Level.VIEW);
  const canOpenProfiles = permissions.can(Cap.USERS_DETAILS, Level.VIEW);
  const canOpenEvents = permissions.can(Cap.EVENTS, Level.VIEW);
  const takesPayments = permissions.hasFeatureAccess(
    "PAYMENT_REQUEST_CREATION"
  );
  const [showPlans, setShowPlans] = useState(false);
  const selfId = user.user_identification;

  // Read again on return to this tab: names and availability are changed on the person's profile.
  const { data: storedTypes } = useGetAgendaTypes(customerId, entityId, {
    enabled: open && !!type,
    refetchOnWindowFocus: "always",
  });
  const stored = storedTypes?.find((item) => item.id === type?.id) ?? type;
  const paymentsBlocked = useAgendaPaymentsBlocked(customerId, entityId, {
    enabled: open,
  });
  const canOpenPaymentModule = permissions.can(Cap.PAYMENT_CONFIG, Level.VIEW);
  const [found, setFound] = useState<Record<string, TypePerson>>({});
  const personSearch = usePersonSearch(customerId, entityId);
  const resetSearch = personSearch.reset;

  const create = useCreateAgendaType(customerId, entityId);
  const update = useUpdateAgendaType(customerId, entityId);
  const isSaving = create.isLoading || update.isLoading;

  const { data: formsData } = useGetSignupFormsList(entityId, {
    enabled: open && !!entityId,
  });
  const forms: { formId: string; name?: string }[] = formsData?.forms ?? [];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<FormValues>({ defaultValues: toValues(type, lang) });

  // Saving someone's hours reads the types again and hands over a new object for the same type: only an
  // opening or another type may reset the form, or the unsaved edits would be lost.
  const resetFor = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!open) {
      resetFor.current = undefined;
      return;
    }
    const id = type?.id ?? null;
    if (resetFor.current === id) return;
    resetFor.current = id;
    reset(toValues(type, lang));
    setFound({});
    resetSearch();
  }, [open, type, lang, reset, resetSearch]);

  const memberIds = watch("members");
  const people: TypePerson[] = memberIds.map(
    (id) =>
      stored?.members.find((m) => m.user_identification === id) ??
      found[id] ?? { user_identification: id, name: null, public_name: null }
  );
  const isFull = memberIds.length >= BOOKING_LIMITS.maxMembers;

  const readable = memberIds.filter((id) => id === selfId || managesOthers);
  const settings = useGetMembersSettings(customerId, entityId, readable, {
    enabled: open,
    refetchOnWindowFocus: "always",
  });
  const availabilityOf = (userId: string): PersonAvailability => {
    const query = settings[readable.indexOf(userId)];
    if (!query) return { state: "hidden" };
    if (query.isLoading) return { state: "loading" };
    if (query.isError) return { state: "failed" };
    return { state: "known", settings: query.data ?? null };
  };

  const setMembers = (ids: string[]) =>
    setValue("members", ids, { shouldValidate: true, shouldDirty: true });
  const addPerson = (person: FoundPerson) => {
    const current = getValues("members");
    const id = person.user_identification;
    if (current.includes(id) || current.length >= BOOKING_LIMITS.maxMembers)
      return;
    setFound((prev) => ({ ...prev, [id]: fromSearch(person) }));
    setMembers([...current, id]);
  };
  const removePerson = (id: string) =>
    setMembers(getValues("members").filter((memberId) => memberId !== id));

  const signupFormId = watch("signup_form_id");
  const imageUrl = watch("image_url");

  const toInput = (values: FormValues): AgendaTypeInput => ({
    title: values.title.trim(),
    location: values.location.trim(),
    description: isHtmlEmpty(values.description) ? "" : values.description,
    image_url: values.image_url || null,
    members: values.members,
    duration_min: durationOf(values.duration_hours, values.duration_minutes),
    min_notice_hours: values.min_notice_hours as number,
    horizon_days: values.horizon_days as number,
    max_upcoming_per_client: values.one_upcoming
      ? type?.max_upcoming_per_client ?? 1
      : null,
    listed: values.listed,
    signup_form_id: values.signup_form_id || null,
    payment: values.paid
      ? {
          amount: inputToCents(values.amount) as number,
          currency: values.currency,
          unpaid: values.unpaid,
        }
      : null,
  });

  const durationMessage = (hours: string, minutes: number) => {
    const problem = durationProblem(hours, minutes);
    if (problem === "notWhole")
      return t("agenda.types.validation.durationWholeHours") as string;
    if (problem === "tooLong")
      return t("agenda.types.validation.durationTooLong", {
        max: BOOKING_LIMITS.maxDurationMin / 60,
      }) as string;
    if (problem === "tooShort")
      return t("agenda.types.validation.durationTooShort", {
        min: BOOKING_LIMITS.minDurationMin,
      }) as string;
    return true;
  };
  const durationNow = durationProblem(
    watch("duration_hours"),
    watch("duration_minutes")
  );
  const tooLong = durationNow === "tooLong";
  const durationInvalid =
    tooLong || durationNow === "notWhole" || !!errors.duration_hours;

  const paid = watch("paid");
  const lostPayments =
    Boolean(type?.payment) && (!takesPayments || paymentsBlocked === "plan");
  const payoutBlocked =
    Boolean(type?.payment) &&
    takesPayments &&
    paymentsBlocked === "payout_account";
  const amountProblem = (value: string) => {
    if (!getValues("paid")) return true;
    const cents = inputToCents(value);
    if (cents === null) return t("agenda.types.drawer.amountInvalid") as string;
    if (cents < PAYMENT_LIMITS.minAmount || cents > PAYMENT_LIMITS.maxAmount)
      return t("agenda.types.drawer.amountRange", {
        min: formatPrice(PAYMENT_LIMITS.minAmount, getValues("currency"), lang),
        max: formatPrice(PAYMENT_LIMITS.maxAmount, getValues("currency"), lang),
      }) as string;
    return true;
  };

  const save = (then: (saved: AgendaType) => void) =>
    handleSubmit((values) => {
      const input = toInput(values);
      const options = {
        onSuccess: (saved: AgendaType) => {
          toast.success(t("agenda.types.drawer.saved") as string);
          then(saved);
        },
        onError: (err: AxiosError<any>) => {
          toast.error(failure(err, "agenda.types.drawer.saveError"));
        },
      };
      if (type) update.mutate({ id: type.id, patch: input }, options);
      else create.mutate(input, options);
    })();

  const onSave = () => save(() => onClose());

  const onCreateForm = () =>
    save((saved) =>
      navigate(
        `/control-panel/signup-forms?booking_type=${encodeURIComponent(
          saved.id
        )}`
      )
    );

  const title = type
    ? (t("agenda.types.drawer.editTitle") as string)
    : (t("agenda.types.drawer.createTitle") as string);

  return (
    <SideDrawer
      open={open}
      // The drawer hears Escape on the whole page, even under the plans.
      onClose={() => {
        if (!showPlans) onClose();
      }}
      title={title}
      icon={HiOutlineCalendar}
      footer={
        <>
          {canEdit && (
            <div className="sticky bottom-0 left-0 right-0 -mx-4 -mb-4 border-t border-gray-200 bg-white px-4 py-3">
              <div className="flex flex-wrap items-center justify-end gap-2">
                <CustomButton
                  variant="secondary"
                  title={t("common.cancel") as string}
                  onClick={onClose}
                  disabled={isSaving}
                />
                <CustomButton
                  variant="primary"
                  title={t("agenda.types.drawer.save") as string}
                  onClick={onSave}
                  isLoading={isSaving}
                  disabled={isSaving}
                />
              </div>
            </div>
          )}
          {showPlans && (
            <PlansModal
              showPlansModal
              setShowPlansModal={setShowPlans}
              targetPlan={PlanTier.ENTERPRISE}
            />
          )}
        </>
      }
    >
      <div className="space-y-6 pb-4">
        <p className="text-sm text-gray-500">
          {t("agenda.types.drawer.description")}
        </p>

        <div>
          <CustomInput
            name="title"
            control={control}
            label={t("agenda.types.drawer.titleLabel") as string}
            placeholder={t("agenda.types.drawer.titlePlaceholder") as string}
            required
            disabled={!canEdit}
            maxLength={BOOKING_LIMITS.maxTitleLength}
            rules={{
              validate: (value: string) =>
                value.trim().length > 0 ||
                (t("agenda.types.validation.titleRequired") as string),
            }}
            error={errors.title}
            helperText={errors.title}
          />
          {canEdit && (
            <CharacterCounter
              currentLength={(watch("title") || "").length}
              maxLength={BOOKING_LIMITS.maxTitleLength}
              className="mt-1"
            />
          )}
        </div>

        <div>
          <CustomInput
            name="location"
            control={control}
            label={t("agenda.types.drawer.locationLabel") as string}
            placeholder={t("agenda.types.drawer.locationPlaceholder") as string}
            disabled={!canEdit}
            maxLength={BOOKING_LIMITS.maxLocationLength}
            aria-describedby={
              canEdit ? "agenda-type-location-helper" : undefined
            }
            hint={
              canEdit && (
                <p
                  id="agenda-type-location-helper"
                  className="mt-1 text-xs text-gray-500"
                >
                  {t("agenda.types.drawer.locationHelper")}
                </p>
              )
            }
          />
          {canEdit && (
            <CharacterCounter
              currentLength={(watch("location") || "").length}
              maxLength={BOOKING_LIMITS.maxLocationLength}
              className="mt-1"
            />
          )}
        </div>

        <section className="space-y-1">
          <h3 className="text-sm font-semibold text-gray-900">
            {t("agenda.types.drawer.descriptionLabel")}
          </h3>
          <p className="text-xs text-gray-500">
            {t("agenda.types.drawer.descriptionHelper")}
          </p>
          <Controller
            name="description"
            control={control}
            rules={{
              validate: (value: string) => {
                const problem = richTextProblem(
                  value,
                  BOOKING_LIMITS.maxDescriptionLength
                );
                if (problem === "tooLong")
                  return t("htmlEditor.tooLong", {
                    count: richTextLength(value),
                    max: BOOKING_LIMITS.maxDescriptionLength,
                  }) as string;
                if (problem === "tooHeavy")
                  return t(
                    "agenda.types.validation.descriptionTooLong"
                  ) as string;
                return true;
              },
            }}
            render={({ field }) => (
              <HtmlEditor
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={!canEdit}
                placeholder={
                  t("agenda.types.drawer.descriptionPlaceholder") as string
                }
                minHeight="160px"
                maxHeight="360px"
                maxLength={BOOKING_LIMITS.maxDescriptionLength}
                showCharacterCount
                className="border-gray-300"
              />
            )}
          />
          {errors.description && (
            <p className="text-xs text-red-600" role="alert">
              {errors.description.message}
            </p>
          )}
        </section>

        {(canEdit || imageUrl) && (
          <section className="space-y-1">
            <h3 className="text-sm font-semibold text-gray-900">
              {t("agenda.types.drawer.imageLabel")}
            </h3>
            <p className="text-xs text-gray-500">
              {t("agenda.types.drawer.imageHelper")}
            </p>
            {canEdit ? (
              <Controller
                name="image_url"
                control={control}
                render={({ field }) => (
                  <EventImageUpload
                    imageUrl={field.value || undefined}
                    onImageUploaded={field.onChange}
                    onImageRemoved={() => field.onChange("")}
                    uploadLabel={t("agenda.types.drawer.imageUpload") as string}
                    alt={watch("title")}
                    upgrade={{
                      title: t("agenda.types.drawer.imageUpgradeTitle"),
                      description: t(
                        "agenda.types.drawer.imageUpgradeDescription"
                      ),
                      benefits: [
                        t("agenda.types.drawer.imageUpgradeBenefit1"),
                        t("agenda.types.drawer.imageUpgradeBenefit2"),
                        t("agenda.types.drawer.imageUpgradeBenefit3"),
                      ],
                    }}
                  />
                )}
              />
            ) : (
              <BookingTypeImage src={imageUrl} alt={watch("title")} />
            )}
          </section>
        )}

        <section>
          <h3 className="text-sm font-semibold text-gray-900">
            {t("agenda.types.drawer.membersLabel")}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {t("agenda.types.drawer.membersHelper")}
          </p>
          <Controller
            name="members"
            control={control}
            rules={{
              validate: (value: string[]) =>
                value.length > 0 ||
                (t("agenda.types.validation.membersRequired") as string),
            }}
            render={() => (
              <TypeMembersField
                people={people}
                availabilityOf={availabilityOf}
                profilePathOf={(id) =>
                  canOpenProfiles ? profilePath(id) : null
                }
                onRemove={canEdit ? removePerson : undefined}
              />
            )}
          />
          {errors.members && (
            <p className="mt-1 text-xs text-red-600" role="alert">
              {errors.members.message}
            </p>
          )}
          {!canOpenProfiles &&
            people.some((p) => p.user_identification !== selfId) && (
              <p className="mt-1 text-xs text-gray-500">
                {t("agenda.types.drawer.profilesNeeded")}
              </p>
            )}

          {canEdit && (
            <div className="mt-4 rounded-md border border-gray-200 p-3">
              <h4 className="mb-2 text-sm font-medium text-gray-900">
                {t("agenda.types.drawer.addMember")}
              </h4>
              {!canSearch ? (
                <p className="text-sm text-gray-600">
                  {t("agenda.types.drawer.searchNeeded")}
                </p>
              ) : (
                <>
                  {isFull && (
                    <p className="mb-3 text-sm text-amber-700" role="status">
                      {t("agenda.types.drawer.membersMax", {
                        max: BOOKING_LIMITS.maxMembers,
                      })}
                    </p>
                  )}
                  <PersonSearchList
                    state={personSearch}
                    pickLabel={t("agenda.types.drawer.addAction")}
                    unavailable={(person) =>
                      memberIds.includes(person.user_identification)
                        ? t("agenda.types.drawer.alreadyOffered")
                        : null
                    }
                    disabled={isFull}
                    onPick={addPerson}
                  />
                </>
              )}
            </div>
          )}
        </section>

        <fieldset className="space-y-1">
          <legend className="block text-sm font-medium text-gray-700">
            {t("agenda.types.drawer.durationLabel")}
          </legend>
          <div className="flex flex-wrap items-center gap-2">
            <Controller
              name="duration_hours"
              control={control}
              rules={{
                validate: (value: string) =>
                  durationMessage(value, getValues("duration_minutes")),
              }}
              render={({ field }) => (
                <input
                  id="agenda-type-duration-hours"
                  type="text"
                  inputMode="numeric"
                  aria-label={t("agenda.types.drawer.durationHours") as string}
                  aria-describedby="agenda-type-duration-problem"
                  aria-invalid={durationInvalid}
                  disabled={!canEdit}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  onBlur={field.onBlur}
                  className={twMerge(fieldClass(durationInvalid), "w-20")}
                />
              )}
            />
            <span className="text-sm text-gray-600" aria-hidden="true">
              {t("agenda.types.drawer.hoursUnit")}
            </span>
            <Controller
              name="duration_minutes"
              control={control}
              render={({ field }) => (
                <select
                  id="agenda-type-duration-minutes"
                  aria-label={
                    t("agenda.types.drawer.durationMinutes") as string
                  }
                  value={field.value}
                  disabled={!canEdit}
                  onChange={(e) => {
                    field.onChange(Number(e.target.value));
                    if (isSubmitted) trigger("duration_hours");
                  }}
                  onBlur={field.onBlur}
                  className={twMerge(fieldClass(false), "w-24 pr-8")}
                >
                  {DURATION_MINUTE_STEPS.map((minutes) => (
                    <option key={minutes} value={minutes}>
                      {String(minutes).padStart(2, "0")}
                    </option>
                  ))}
                </select>
              )}
            />
            <span className="text-sm text-gray-600" aria-hidden="true">
              {t("agenda.types.drawer.minutesUnit")}
            </span>
          </div>
          <div id="agenda-type-duration-problem">
            {tooLong ? (
              <p className="text-xs text-red-600" role="alert">
                {t("agenda.types.validation.durationTooLong", {
                  max: BOOKING_LIMITS.maxDurationMin / 60,
                })}{" "}
                {canOpenEvents && (
                  <a
                    href={EVENTS_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-md font-medium text-blue-600 hover:text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    {t("agenda.types.drawer.openEvents")}
                    <HiOutlineExternalLink size={12} aria-hidden="true" />
                  </a>
                )}
              </p>
            ) : durationNow === "notWhole" ? (
              <p className="text-xs text-red-600" role="alert">
                {t("agenda.types.validation.durationWholeHours")}
              </p>
            ) : (
              errors.duration_hours && (
                <p className="text-xs text-red-600" role="alert">
                  {errors.duration_hours.message}
                </p>
              )
            )}
          </div>
        </fieldset>

        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label
              htmlFor="agenda-type-notice"
              className="block text-sm font-medium text-gray-700"
            >
              {t("agenda.types.drawer.noticeLabel")}
            </label>
            <Controller
              name="min_notice_hours"
              control={control}
              rules={{
                validate: (value: number | "") =>
                  inRange(value, 0, BOOKING_LIMITS.maxNoticeHours) ||
                  (t("agenda.types.validation.noticeRange", {
                    max: BOOKING_LIMITS.maxNoticeHours,
                  }) as string),
              }}
              render={({ field }) => (
                <input
                  id="agenda-type-notice"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={BOOKING_LIMITS.maxNoticeHours}
                  disabled={!canEdit}
                  value={field.value}
                  onChange={(e) => field.onChange(toInteger(e.target.value))}
                  onBlur={field.onBlur}
                  className={fieldClass(!!errors.min_notice_hours)}
                />
              )}
            />
            <p className="text-xs text-gray-500">
              {t("agenda.types.drawer.noticeHelper")}
            </p>
            {errors.min_notice_hours && (
              <p className="text-xs text-red-600" role="alert">
                {errors.min_notice_hours.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label
              htmlFor="agenda-type-horizon"
              className="block text-sm font-medium text-gray-700"
            >
              {t("agenda.types.drawer.horizonLabel")}
            </label>
            <Controller
              name="horizon_days"
              control={control}
              rules={{
                validate: (value: number | "") =>
                  inRange(value, 1, BOOKING_LIMITS.maxHorizonDays) ||
                  (t("agenda.types.validation.horizonRange", {
                    max: BOOKING_LIMITS.maxHorizonDays,
                  }) as string),
              }}
              render={({ field }) => (
                <input
                  id="agenda-type-horizon"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={BOOKING_LIMITS.maxHorizonDays}
                  disabled={!canEdit}
                  value={field.value}
                  onChange={(e) => field.onChange(toInteger(e.target.value))}
                  onBlur={field.onBlur}
                  className={fieldClass(!!errors.horizon_days)}
                />
              )}
            />
            <p className="text-xs text-gray-500">
              {t("agenda.types.drawer.horizonHelper")}
            </p>
            {errors.horizon_days && (
              <p className="text-xs text-red-600" role="alert">
                {errors.horizon_days.message}
              </p>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {t("agenda.types.drawer.paymentTitle")}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {t("agenda.types.drawer.paymentHelper")}
            </p>
          </div>
          {payoutBlocked && paid && (
            <AlertMessage color="warning">
              <p>{t("agenda.types.drawer.payoutAccountBlocked")}</p>
              {canOpenPaymentModule && (
                <a
                  href={PAYMENT_MODULE_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 rounded-md font-medium text-blue-600 hover:text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  {t("agenda.types.drawer.openPaymentModule")}
                  <HiOutlineExternalLink size={14} aria-hidden="true" />
                </a>
              )}
            </AlertMessage>
          )}
          {lostPayments && paid && (
            <AlertMessage color="warning">
              <p>{t("agenda.types.drawer.paymentLost")}</p>
              {canEdit && (
                <CustomButton
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  title={t("agenda.types.drawer.removePrice") as string}
                  onClick={() => setValue("paid", false, { shouldDirty: true })}
                />
              )}
            </AlertMessage>
          )}
          <Controller
            name="paid"
            control={control}
            render={({ field }) => (
              <OptionToggleRow
                icon={HiOutlineCreditCard}
                title={t("agenda.types.drawer.paidTitle")}
                description={t("agenda.types.drawer.paidDescription")}
                enabled={field.value}
                onChange={field.onChange}
                disabled={!canEdit || (!takesPayments && !field.value)}
                className="border-y border-gray-100"
              />
            )}
          />
          {!takesPayments && !lostPayments && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
              <span>{t("agenda.types.drawer.paymentLocked")}</span>
              <UnlockBadge onClick={() => setShowPlans(true)} />
            </div>
          )}
          {paid && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
                <div className="space-y-1">
                  <label
                    htmlFor="agenda-type-amount"
                    className="block text-sm font-medium text-gray-900"
                  >
                    {t("agenda.types.drawer.amountLabel")}
                  </label>
                  <Controller
                    name="amount"
                    control={control}
                    rules={{ validate: amountProblem }}
                    render={({ field }) => (
                      <input
                        id="agenda-type-amount"
                        type="text"
                        inputMode="decimal"
                        value={field.value}
                        disabled={!canEdit || !takesPayments}
                        placeholder={
                          t("agenda.types.drawer.amountPlaceholder") as string
                        }
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        aria-invalid={Boolean(errors.amount)}
                        aria-describedby={
                          errors.amount ? "agenda-type-amount-error" : undefined
                        }
                        className={fieldClass(Boolean(errors.amount))}
                      />
                    )}
                  />
                  {errors.amount && (
                    <p
                      id="agenda-type-amount-error"
                      className="text-xs text-red-600"
                      role="alert"
                    >
                      {errors.amount.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor="agenda-type-currency"
                    className="block text-sm font-medium text-gray-900"
                  >
                    {t("agenda.types.drawer.currencyLabel")}
                  </label>
                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <select
                        id="agenda-type-currency"
                        value={field.value}
                        disabled={!canEdit || !takesPayments}
                        onChange={(e) =>
                          field.onChange(e.target.value as PaymentCurrency)
                        }
                        onBlur={field.onBlur}
                        className={twMerge(fieldClass(false), "pr-8")}
                      >
                        {PAYMENT_CURRENCIES.map((code) => (
                          <option key={code} value={code}>
                            {code}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                </div>
              </div>
              <Controller
                name="unpaid"
                control={control}
                render={({ field }) => (
                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium text-gray-900">
                      {t("agenda.types.drawer.unpaidLabel")}
                    </legend>
                    {(["required", "accepted"] as const).map((rule) => (
                      <label
                        key={rule}
                        className={twMerge(
                          "flex cursor-pointer items-start gap-3 rounded-md border p-3",
                          field.value === rule
                            ? "border-blue-300 bg-blue-50"
                            : "border-gray-200"
                        )}
                      >
                        <input
                          type="radio"
                          name="agenda-type-unpaid"
                          value={rule}
                          checked={field.value === rule}
                          disabled={!canEdit || !takesPayments}
                          onChange={() => field.onChange(rule)}
                          className="mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-gray-900">
                            {t(`agenda.types.drawer.unpaid.${rule}`)}
                          </span>
                          <span className="block text-xs text-gray-500">
                            {t(`agenda.types.drawer.unpaid.${rule}Help`)}
                          </span>
                        </span>
                      </label>
                    ))}
                  </fieldset>
                )}
              />
            </div>
          )}
        </section>

        <div>
          <Controller
            name="listed"
            control={control}
            render={({ field }) => (
              <OptionToggleRow
                icon={HiOutlineGlobeAlt}
                title={t("agenda.types.drawer.listedTitle")}
                description={t("agenda.types.drawer.listedDescription")}
                enabled={field.value}
                onChange={field.onChange}
                disabled={!canEdit}
                className="border-t border-gray-100"
              />
            )}
          />

          <Controller
            name="one_upcoming"
            control={control}
            render={({ field }) => (
              <OptionToggleRow
                icon={HiOutlineUser}
                title={t("agenda.types.drawer.oneUpcomingTitle")}
                description={t("agenda.types.drawer.oneUpcomingDescription")}
                enabled={field.value}
                onChange={field.onChange}
                disabled={!canEdit}
                className="border-y border-gray-100"
              />
            )}
          />
        </div>

        <section className="space-y-1">
          <label
            htmlFor="agenda-type-signup-form"
            className="block text-sm font-semibold text-gray-900"
          >
            {t("agenda.types.drawer.signupFormLabel")}
          </label>
          <p className="text-xs text-gray-500">
            {signupFormId
              ? t("agenda.types.drawer.signupFormHelper")
              : t("agenda.types.drawer.signupFormNoneHelper")}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="min-w-0 flex-1 sm:basis-64">
              <Controller
                name="signup_form_id"
                control={control}
                render={({ field }) => (
                  <select
                    id="agenda-type-signup-form"
                    value={field.value}
                    disabled={!canEdit}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    className={twMerge(fieldClass(false), "pr-8")}
                  >
                    <option value="">
                      {t("agenda.types.drawer.signupFormNone")}
                    </option>
                    {forms.map((form) => (
                      <option key={form.formId} value={form.formId}>
                        {form.name || t("signupForms.unnamedForm")}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>
            {canEdit && canCreateForm && (
              <div className="flex flex-wrap items-center gap-2">
                <CustomButton
                  variant="secondary"
                  icon="plus"
                  title={t("agenda.types.drawer.createForm") as string}
                  onClick={onCreateForm}
                  disabled={isSaving}
                />
              </div>
            )}
          </div>
          {canEdit && canCreateForm && (
            <p className="text-xs text-gray-500">
              {t("agenda.types.drawer.createFormHelper")}
            </p>
          )}
        </section>
      </div>
    </SideDrawer>
  );
};

export default AppointmentTypeDrawer;
